// src\components\dashboard\TVBatchDetail.jsx
import React from 'react';
import { Clock, CheckCircle, AlertTriangle, Clock3, Activity } from 'lucide-react';

/**
 * Composant de détail de fournée optimisé pour affichage sur téléviseur
 * Conçu pour une meilleure visibilité à distance
 */
const TVBatchDetail = ({ batch, orders, resource }) => {
  if (!batch) return null;
  
  // Calculer les statistiques de la fournée
  const completedCount = orders.filter(order => order.status === 'completed').length;
  const inProgressCount = orders.filter(order => order.status === 'in-progress').length;
  const waitingCount = orders.filter(order => order.status === 'waiting').length;
  const alertsCount = orders.filter(order => order.alert).length;
  
  // Déterminer si des contraintes sont dépassées
  const totalVacuumPorts = orders.reduce((sum, order) => sum + order.vacuumPorts, 0);
  const totalTc = orders.reduce((sum, order) => sum + order.tc, 0);
  const vacuumPortsExceeded = resource && totalVacuumPorts > resource.maxVacuumPorts;
  const tcExceeded = resource && totalTc > resource.maxTc;
  
  // Fonction pour obtenir la couleur du cycle
  const getCycleColor = (cycleName) => {
    switch(cycleName) {
      case 'A42': return 'border-blue-500 bg-blue-50';
      case 'A35': return 'border-purple-500 bg-purple-50';
      case 'E25': return 'border-green-500 bg-green-50';
      case 'A29': return 'border-red-500 bg-red-50';
      case 'E28': return 'border-yellow-500 bg-yellow-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };
  
  // Fonction pour obtenir la couleur de l'état d'un OF
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'in-progress': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'waiting': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  // Fonction pour obtenir l'icône d'état
  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle size={20} className="text-green-500" />;
      case 'in-progress': return <Activity size={20} className="text-blue-500" />;
      case 'waiting': return <Clock3 size={20} className="text-yellow-500" />;
      default: return <Clock3 size={20} className="text-gray-500" />;
    }
  };
  
  // Fonction pour obtenir le libellé d'état
  const getStatusLabel = (status) => {
    switch(status) {
      case 'completed': return 'Terminé';
      case 'in-progress': return 'En cours';
      case 'waiting': return 'En attente';
      default: return 'Non démarré';
    }
  };
  
  // Calculer le pourcentage de progression
  const progressPercentage = orders.length > 0 
    ? Math.round((completedCount / orders.length) * 100)
    : 0;
  
  // Obtenir la couleur de la barre de progression
  const getProgressBarColor = (percentage) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className={`rounded-lg shadow-lg overflow-hidden ${getCycleColor(batch.cycleName)}`}>
      {/* En-tête de la fournée avec couleur selon le cycle */}
      <div className="p-4 border-b-4" style={{ borderColor: getBorderColor(batch.cycleName) }}>
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">{batch.batchNumber}</h2>
            <div className="text-lg font-medium">{batch.cycleName} - {batch.code}</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-medium">{resource?.name || 'Moyen inconnu'}</div>
            <div className="flex items-center justify-end text-gray-600 mt-1">
              <Clock size={18} className="mr-1" />
              <span className="text-base">{batch.startTime} - {batch.endTime}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Barre de progression avec pourcentage */}
      <div className="p-3 bg-white border-b">
        <div className="flex justify-between items-center mb-1">
          <div className="text-lg font-bold">Progression: {progressPercentage}%</div>
          <div className="text-sm">{completedCount}/{orders.length} OF</div>
        </div>
        <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-6 ${getProgressBarColor(progressPercentage)}`}
            style={{ width: `${progressPercentage}%` }}
          ></div>
          <div className="absolute inset-0 flex items-center justify-center text-white font-medium">
            {progressPercentage}%
          </div>
        </div>
      </div>
      
      {/* Statistiques et contraintes */}
      <div className="p-3 bg-white border-b">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" className="mr-2 text-purple-600">
              <circle cx="12" cy="12" r="10" fill="none" strokeWidth="2"></circle>
              <path d="M8 12h8" strokeWidth="2"></path>
              <path d="M12 8v8" strokeWidth="2"></path>
            </svg>
            <div className={`${vacuumPortsExceeded ? 'text-red-600 font-bold' : ''}`}>
              Prises de vide: <span className="font-bold">{totalVacuumPorts}/{resource?.maxVacuumPorts || '?'}</span>
              {vacuumPortsExceeded && <span className="text-red-600 ml-1">(Dépassé!)</span>}
            </div>
          </div>
          <div className="flex items-center">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" className="mr-2 text-red-600">
              <path d="M12 2v6M12 22v-6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M22 12h-6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" fill="none" strokeWidth="2"></path>
            </svg>
            <div className={`${tcExceeded ? 'text-red-600 font-bold' : ''}`}>
              Thermocouples: <span className="font-bold">{totalTc}/{resource?.maxTc || '?'}</span>
              {tcExceeded && <span className="text-red-600 ml-1">(Dépassé!)</span>}
            </div>
          </div>
        </div>
      </div>
      
      {/* Liste des OF */}
      <div className="p-2 bg-white max-h-96 overflow-y-auto">
        <div className="grid grid-cols-1 gap-2">
          {orders.map(order => (
            <div 
              key={order.id}
              className={`p-3 rounded-lg border flex items-center ${getStatusColor(order.status)}`}
            >
              <div className="mr-3">
                {getStatusIcon(order.status)}
              </div>
              <div className="flex-grow">
                <div className="flex justify-between">
                  <div className="font-bold text-base">{order.id}</div>
                  <div className="font-medium">{getStatusLabel(order.status)}</div>
                </div>
                <div className="text-base">{order.name}</div>
                {order.alert && (
                  <div className="flex items-center mt-1 text-red-600">
                    <AlertTriangle size={16} className="mr-1" />
                    <span>{order.alert}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {orders.length === 0 && (
            <div className="py-8 text-center text-gray-500 text-lg">
              Aucun OF assigné à cette fournée
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Fonction pour obtenir la couleur de bordure selon le cycle
const getBorderColor = (cycleName) => {
  switch(cycleName) {
    case 'A42': return '#3b82f6'; // blue-500
    case 'A35': return '#8b5cf6'; // purple-500
    case 'E25': return '#22c55e'; // green-500
    case 'A29': return '#ef4444'; // red-500
    case 'E28': return '#eab308'; // yellow-500
    default: return '#6b7280'; // gray-500
  }
};

export default TVBatchDetail;